import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Loader2, Minus, Plus, ShoppingCart, Headset } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import CouponOffers, { describeCoupon, type PublicCoupon } from "./CouponOffers";
import { useCart } from "@/contexts/CartContext";
import {
  formatINR,
  MAX_QUANTITY_PER_ITEM,
  type CatalogPlan,
  type CatalogProduct,
} from "@/lib/cart-pricing";

/*
 * NOTE: the previous date-first "select plan → pick date → Book Now" implementation
 * is preserved at src/components/legacy-RentalSelector.tsx.bak for reference.
 * The rental start date is now chosen once during checkout on /cart.
 */

const fallbackPlans: CatalogPlan[] = [
  { id: 1, name: "1_DAY", days: 1, price: 799, label: "1 Day", tagline: "Quick getaway" },
  { id: 2, name: "3_DAYS", days: 3, price: 1999, label: "3 Days", tagline: "Weekend escape" },
  { id: 3, name: "7_DAYS", days: 7, price: 3499, label: "7 Days", tagline: "Full week adventure" },
];

const fallbackProducts: CatalogProduct[] = [
  {
    id: 1,
    slug: "meta-quest-3",
    name: "Meta Quest 3",
    short_description: "Flagship mixed reality with 4K+ Infinite Display and full-colour passthrough.",
    features: ["4K+ Infinite Display", "Snapdragon XR2 Gen 2", "Mixed reality passthrough"],
    image_url: null,
    badge: "MOST POPULAR",
    plans: fallbackPlans,
  },
];

const RentalSelector = () => {
  const COUPON_HINT_KEY = "vrrentals.coupon.hint";
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<string>("");
  const [publicCoupons, setPublicCoupons] = useState<PublicCoupon[]>([]);
  const planRef = useRef<HTMLDivElement>(null);
  const { addItem, count } = useCart();

  // Remember the offer the visitor tapped so checkout can prefill it.
  useEffect(() => {
    const code = selectedCoupon || "";
    try {
      if (code) localStorage.setItem(COUPON_HINT_KEY, code);
    } catch {
      /* storage unavailable */
    }
  }, [selectedCoupon]);

  useEffect(() => {
    axios
      .get("/.netlify/functions/get-products")
      .then((res) => {
        const data = res.data?.data;
        setProducts(Array.isArray(data) && data.length > 0 ? data : fallbackProducts);
      })
      .catch(() => {
        console.warn("Using fallback catalog (API unavailable)");
        setProducts(fallbackProducts);
      })
      .finally(() => setLoadingCatalog(false));
  }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) ?? null,
    [products, selectedProductId],
  );
  const selectedPlan = useMemo(
    () => selectedProduct?.plans.find((p) => p.id === selectedPlanId) ?? null,
    [selectedProduct, selectedPlanId],
  );

  const handleProductSelect = useCallback((product: CatalogProduct) => {
    setSelectedProductId(product.id);
    setSelectedPlanId(null);
    setQuantity(1);
    setTimeout(() => {
      planRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 180);
  }, []);

  const bestValuePlanId = useMemo(() => {
    const plans = selectedProduct?.plans ?? [];
    if (plans.length === 0) return null;
    return plans.reduce((best, p) => (p.price / p.days < best.price / best.days ? p : best)).id;
  }, [selectedProduct]);

  const bestOffer = useMemo(() => {
    if (!selectedPlan || publicCoupons.length === 0) return null;
    const eligible = publicCoupons.filter((c) => (c.min_order_value || 0) <= selectedPlan.price * quantity);
    return eligible[0] ?? null;
  }, [publicCoupons, selectedPlan, quantity]);

  const handleAddToCart = () => {
    if (!selectedProduct) {
      toast.error("Please choose a VR headset first");
      return;
    }
    if (!selectedPlan) {
      toast.error("Please choose a rental plan");
      return;
    }
    addItem(selectedProduct, selectedPlan, quantity);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
    toast.success(`${selectedProduct.name} — ${selectedPlan.label || selectedPlan.name} added to cart`, {
      description: "Keep browsing or head to your cart to check out.",
    });
  };

  return (
    <section id="rent" className="py-24 md:py-36 px-6 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />
      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
            Rent a VR Headset — Choose Your Device
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            All rentals include free delivery in Pune &amp; Mumbai, setup guide,
            preloaded games, and free return pickup.
          </p>
        </motion.div>

        {/* Live offers */}
        <CouponOffers onSelect={setSelectedCoupon} onLoaded={setPublicCoupons} />

        {/* Step 1: Device selection */}
        <div className="mb-4 flex items-center gap-3">
          <span className="font-heading text-xs font-bold tracking-widest text-primary uppercase">Step 1</span>
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground">Choose Your VR Headset</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {loadingCatalog
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="bg-card border-border">
                  <CardContent className="p-6 space-y-4">
                    <Skeleton className="h-6 w-28" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))
            : products.map((product, i) => {
                const isSelected = selectedProductId === product.id;
                const from = product.plans.length
                  ? Math.min(...product.plans.map((p) => p.price))
                  : 0;
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.06 }}
                  >
                    <Card
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => handleProductSelect(product)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleProductSelect(product);
                        }
                      }}
                      className={`relative h-full cursor-pointer transition-all duration-300 bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        isSelected
                          ? "border-primary ring-2 ring-primary/30 shadow-[var(--shadow-glow-magenta)]"
                          : "border-border hover:border-primary/50 hover:-translate-y-0.5 hover:shadow-md"
                      }`}
                    >
                      {product.badge && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground font-heading text-[10px] tracking-wider px-3 py-1 whitespace-nowrap">
                          {product.badge}
                        </Badge>
                      )}
                      <CardContent className="p-6 pt-8 space-y-4">
                        <div className="relative w-full aspect-[4/3] rounded-lg border border-border bg-muted/30 overflow-hidden flex items-center justify-center">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              loading="lazy"
                              className="w-full h-full object-contain object-center"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-lg border border-border flex items-center justify-center text-primary">
                              <Headset className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-heading text-lg font-bold text-foreground">{product.name}</div>
                          {product.short_description && (
                            <p className="font-body text-sm text-muted-foreground mt-1.5 leading-relaxed">
                              {product.short_description}
                            </p>
                          )}
                        </div>
                        {product.features?.length > 0 && (
                          <ul className="space-y-1.5 font-body text-xs text-muted-foreground">
                            {product.features.slice(0, 3).map((f) => (
                              <li key={f} className="flex items-start gap-2">
                                <Check className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="pt-2 border-t border-border flex items-center justify-between">
                          <span className="font-body text-xs text-muted-foreground">
                            From {formatINR(from)}
                          </span>
                          <span
                            className={`font-heading text-xs font-semibold ${
                              isSelected ? "text-primary" : "text-muted-foreground"
                            }`}
                          >
                            {isSelected
                              ? `Choose ${product.name.replace(/^Meta\s+/, "")} \u2713`
                              : `Choose ${product.name.replace(/^Meta\s+/, "")} \u2192`}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
        </div>

        {/* Comparison strip */}
        {!loadingCatalog && products.length > 1 && (
          <div className="mb-14 rounded-xl border border-border bg-card/50 p-5">
            <p className="font-heading text-sm font-semibold text-foreground mb-3">
              Not sure which headset to choose?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} className="font-body text-sm">
                  <div className="text-foreground font-semibold">{p.name}</div>
                  <div className="text-muted-foreground text-xs mt-1">
                    {p.badge ? `Best for: ${p.badge.toLowerCase()}` : p.features?.[0] || "All-round VR fun"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Included features */}
        <div className="flex flex-wrap justify-center gap-4 mb-14 text-sm text-muted-foreground font-body">
          <span>✓ Free delivery</span>
          <span>✓ Preloaded games</span>
          <span>✓ Damage protection</span>
          <span>✓ Free return pickup</span>
        </div>

        {/* Step 2: Plans for the selected device */}
        <AnimatePresence>
          {selectedProduct && (
            <motion.div
              ref={planRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="mb-5 flex items-center gap-3">
                <span className="font-heading text-xs font-bold tracking-widest text-primary uppercase">Step 2</span>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground">
                  Choose a Plan for {selectedProduct.name}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {selectedProduct.plans.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  const isBestValue = plan.id === bestValuePlanId;
                  return (
                    <Card
                      key={plan.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedPlanId(plan.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedPlanId(plan.id);
                        }
                      }}
                      className={`relative cursor-pointer transition-all duration-300 bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        isSelected
                          ? "border-primary ring-2 ring-primary/30 shadow-[var(--shadow-glow-magenta)]"
                          : isBestValue
                            ? "border-accent/50 shadow-[var(--shadow-glow-teal)]"
                            : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      {isBestValue && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground font-heading text-xs px-3 py-1">
                          Best Value
                        </Badge>
                      )}
                      <CardContent className="p-6 pt-8 text-center space-y-3">
                        <div className="font-heading text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          {plan.label || plan.name}
                        </div>
                        <div className="font-heading text-4xl font-bold text-foreground">
                          {formatINR(plan.price)}
                        </div>
                        {plan.tagline && (
                          <div className="font-body text-sm text-muted-foreground">{plan.tagline}</div>
                        )}
                        <div className="text-xs text-muted-foreground font-body">
                          {formatINR(Math.round(plan.price / plan.days))}/day · {plan.days}{" "}
                          {plan.days === 1 ? "day" : "days"}
                        </div>
                        <div
                          className={`w-full py-2.5 rounded-full font-heading text-sm font-semibold transition-all ${
                            isSelected
                              ? "bg-primary text-primary-foreground glow-magenta"
                              : "bg-muted text-foreground"
                          }`}
                        >
                          {isSelected ? (
                            <span className="flex items-center justify-center gap-2">
                              <Check className="w-4 h-4" /> Selected
                            </span>
                          ) : (
                            "Select Plan"
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 3: Quantity + Add to cart */}
        <AnimatePresence>
          {selectedProduct && selectedPlan && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.3 }}
              className="mt-10 p-6 rounded-2xl bg-card border border-border shadow-lg"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="font-heading text-xs font-bold tracking-widest text-primary uppercase mb-1.5">
                    Step 3
                  </div>
                  <div className="font-heading text-lg font-bold text-foreground">
                    {selectedProduct.name} · {selectedPlan.label || selectedPlan.name}
                  </div>
                  <div className="font-body text-sm text-muted-foreground mt-1">
                    {formatINR(selectedPlan.price)} × {quantity} ={" "}
                    <span className="text-foreground font-semibold">
                      {formatINR(selectedPlan.price * quantity)}
                    </span>{" "}
                    <span className="text-xs">+ GST at checkout</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <div className="flex items-center gap-3 self-start sm:self-auto rounded-full border border-border px-2 py-1.5">
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span aria-live="polite" className="font-heading text-base font-bold text-foreground w-6 text-center">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY_PER_ITEM, q + 1))}
                      disabled={quantity >= MAX_QUANTITY_PER_ITEM}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.97 }}
                    onClick={handleAddToCart}
                    className="font-heading text-base font-semibold px-8 py-3.5 rounded-full bg-primary text-primary-foreground glow-magenta transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {justAdded ? (
                      <>
                        <Check className="w-4 h-4" /> Added
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" /> Add to Cart
                      </>
                    )}
                  </motion.button>
                </div>
              </div>

              {bestOffer && (
                <p className="mt-4 font-body text-xs text-accent">
                  Apply <span className="font-semibold">{bestOffer.code}</span> at checkout — {describeCoupon(bestOffer)}
                </p>
              )}

              {count > 0 && (
                <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
                  <span className="font-body text-sm text-muted-foreground">
                    {count} {count === 1 ? "headset" : "headsets"} in your cart
                  </span>
                  <Link
                    to="/cart"
                    className="font-heading text-sm font-semibold px-6 py-2.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  >
                    View Cart & Checkout
                  </Link>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {loadingCatalog && (
          <div className="flex justify-center mt-8">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        )}

        {selectedCoupon && (
          <p className="sr-only" aria-live="polite">
            Coupon {selectedCoupon} will be applied at checkout
          </p>
        )}
      </div>
    </section>
  );
};

export default RentalSelector;
